import { useHandler } from "@redotech/react-util/hook";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { TriggerStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { ConditionDataSource } from "@redotech/redo-flows-common/steps";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import { memo, useMemo } from "react";
import {
  ConditionSummarySegment,
  getConditionSummary,
} from "../../nodes/condition-summary";
import { useConditionValueLabels } from "../../nodes/condition-value-labels";
import {
  DeepPartial,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../../steps";
import {
  TriggerDataConditionInspector,
  triggerDataExpressionIsValid,
} from "../condition";
import {
  TriggerConditionCard,
  TriggerConditionsSection,
} from "./trigger-conditions-section";

type EntryCondition = DeepPartial<
  NonNullable<TriggerStep["entryConditions"]>["conditions"][number]
>;

export const EntryConditionsInspector = memo(function EntryConditionsInspector({
  step,
  setStep,
  schema,
}: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>) {
  const { t } = useTranslation();
  const conditions: EntryCondition[] = step.entryConditions?.conditions ?? [];
  const conjunctionMode =
    step.entryConditions?.conjunctionMode ?? ConjunctionMode.AND;

  const setConditions = useHandler((conditions: EntryCondition[]) => {
    setStep({ ...step, entryConditions: { conjunctionMode, conditions } });
  });

  const addNewCondition = useHandler(() => {
    setConditions([...conditions, { _id: new ObjectId().toString() }]);
  });

  const setConjunctionMode = useHandler((conjunctionMode: ConjunctionMode) => {
    setStep({ ...step, entryConditions: { conditions, conjunctionMode } });
  });

  return (
    <TriggerConditionsSection
      addButtonText={t("Entry condition")}
      addCondition={addNewCondition}
      conditionCount={conditions.length}
      conjunctionMode={conjunctionMode}
      conjunctionText={t(
        "of these conditions must be met to start this automation",
      )}
      setConjunctionMode={setConjunctionMode}
      singleConditionText={t(
        "Only start this automation when the trigger matches",
      )}
      title={t("Entry conditions")}
    >
      {conditions.map((condition, index) => (
        <EntryConditionBlock
          condition={condition}
          key={condition._id}
          removeCondition={() =>
            setConditions(conditions.filter((_, i) => i !== index))
          }
          schema={schema}
          setCondition={(newCondition) =>
            setConditions(
              conditions.map((existing, i) =>
                i === index ? newCondition : existing,
              ),
            )
          }
        />
      ))}
      {!!conditions.length && (
        <Text fontSize="xxs" textColor="secondary">
          {t(
            "Trigger-specific fields only. To filter on customer attributes or activity, use skip conditions.",
          )}
        </Text>
      )}
    </TriggerConditionsSection>
  );
});

const EntryConditionBlock = memo(function EntryConditionBlock({
  condition,
  setCondition,
  removeCondition,
  schema,
}: {
  condition: EntryCondition;
  setCondition: (condition: EntryCondition) => void;
  removeCondition: () => void;
  schema: Schema;
}) {
  const { t } = useTranslation();
  // The summary and value labels read a full condition, which entry conditions
  // narrow to trigger data.
  const triggerDataCondition = useMemo(
    () => ({
      dataSource: ConditionDataSource.TriggerData as const,
      schemaBooleanExpression: condition.schemaBooleanExpression,
    }),
    [condition.schemaBooleanExpression],
  );
  const valueLabels = useConditionValueLabels(triggerDataCondition);

  const conditionSummary = useMemo(
    (): ConditionSummarySegment[] => [
      { kind: "text", text: t("Starts when") },
      ...getConditionSummary(triggerDataCondition, valueLabels, schema),
    ],
    [triggerDataCondition, valueLabels, schema, t],
  );

  return (
    <TriggerConditionCard
      isValid={triggerDataExpressionIsValid(condition.schemaBooleanExpression)}
      label={t("Entry condition")}
      removeCondition={removeCondition}
      summary={conditionSummary}
    >
      <TriggerDataConditionInspector
        expression={condition.schemaBooleanExpression}
        schema={schema}
        setExpression={(schemaBooleanExpression) =>
          setCondition({
            ...condition,
            // The recursive array-match expression gives these two identical types separate identities.
            schemaBooleanExpression:
              schemaBooleanExpression as EntryCondition["schemaBooleanExpression"],
          })
        }
      />
    </TriggerConditionCard>
  );
});
